import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail } from "lucide-react";
import { useLang } from "@/i18n";
import { NaguaraLogo } from "@/components/BrandMark";
import { NewsletterForm } from "@/components/NewsletterForm";
import { LEGAL_SLUGS } from "@/lib/legalDrafts";
import { CONTACT_EMAIL, INSTAGRAM_URL } from "@/lib/config";

const Footer = () => {
  const { t } = useLang();

  const links = [
    { id: "coffees", to: "/#coffees", label: t.nav.coffees },
    { id: "hosteleria", to: "/hosteleria", label: t.nav.horeca },
    { id: "academia", to: "/formacion", label: t.nav.academia },
    { id: "story", to: "/historia", label: t.nav.story },
    { id: "contact", to: "/contacto", label: t.nav.contact },
  ];

  return (
    <footer
      data-testid="footer"
      id="contact"
      className="bg-[#121212] text-[#F5F1E8] pt-20 pb-10 px-6 md:px-12 lg:px-20 scroll-mt-[104px]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-10 pb-14 border-b border-[#F5F1E8]/15">
          {/* Brand + newsletter (real signup, notified by email to the team). */}
          <div className="lg:col-span-6">
            <p className="font-display text-xl font-bold mb-3">{t.footer.slogan}</p>

            <p className="text-[#E6D9B6]/70 max-w-md leading-relaxed">{t.footer.newsletter}</p>
            <NewsletterForm />
          </div>

          {/* Explore */}
          <div className="lg:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#E6D9B6]/70 mb-4">
              {t.footer.explore}
            </p>
            <ul className="space-y-1">
              {links.map((l) => (
                <li key={l.id}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.id}`}
                    className="flex items-center min-h-[44px] text-[#F5F1E8]/80 hover:text-[#F4D35E] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E] rounded"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + social */}
          <div className="lg:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#E6D9B6]/70 mb-4">
              {t.footer.contactTitle}
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="footer-email-link"
              className="inline-flex items-center min-h-[44px] text-[#F5F1E8]/80 hover:text-[#F4D35E] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E] rounded"
            >
              {CONTACT_EMAIL}
            </a>
            {/* Commercial location. No map link until the full postal address is confirmed. */}
            <p data-testid="footer-location" className="text-[#E6D9B6]/70 text-sm mt-2">
              {t.footer.location}
            </p>
            <div className="flex gap-3 mt-5">
              {/* Only accounts confirmed by the brand. TikTok / WhatsApp stay hidden
                  until their real links exist: no icon leads to a dead page. */}
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t.footer.igAria}
                title={t.footer.igAria}
                data-testid="footer-instagram"
                className="w-11 h-11 rounded-full border border-[#F5F1E8]/25 flex items-center justify-center transition-all hover:bg-[#F4D35E] hover:text-[#3B2523] hover:border-[#F4D35E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E]"
              >
                <Instagram size={17} />
              </a>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                aria-label={t.footer.mailAria}
                title={t.footer.mailAria}
                data-testid="footer-email"
                className="w-11 h-11 rounded-full border border-[#F5F1E8]/25 flex items-center justify-center transition-all hover:bg-[#F4D35E] hover:text-[#3B2523] hover:border-[#F4D35E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E]"
              >
                <Mail size={17} />
              </a>
            </div>
          </div>
        </div>

        <nav aria-label={t.legal.footerTitle} className="pt-10">
          <ul className="flex flex-wrap gap-x-6 gap-y-1">
            {LEGAL_SLUGS.map((slug) => (
              <li key={slug}>
                <Link
                  to={`/legal/${slug}`}
                  data-testid={`footer-legal-${slug}`}
                  className="flex items-center min-h-[44px] text-[#E6D9B6]/70 text-sm hover:text-[#F4D35E] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E] rounded"
                >
                  {t.legal.items[slug]}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-6">
          <NaguaraLogo variant="beige" width={320} className="w-[220px] sm:w-[300px] lg:w-[340px]" />
          <p className="text-[#E6D9B6]/70 text-sm text-center sm:text-right">
            © {new Date().getFullYear()} NAGUARÀ. {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
